import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageEnum } from '../models/enums/local-storage.enum';
import { Login, VerifyOTP } from '../models/login';
import { LocalStorageService } from './local-storage.service';
import { RequestService } from './request.service';
import { TableStateService } from './table-state.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authChange$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.isAuth);
  authStatus: boolean = this.isAuth;
  constructor(
    private router: Router,
    private requestService: RequestService,
    private localStorageService: LocalStorageService,
    private tableStateService: TableStateService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.authStatus != this.isAuth) {
          this.markStatusChange();
        }
      }
    });
  }

  login(data: { username: string; password: string }): Observable<Login> {
    return this.requestService.postJSON<Login>('/studio/account/login', { data }).pipe(
      map(res => {
        if (res?.token) this.setToken(res);
        this.markStatusChange();
        return res;
      })
    );
  }

  setToken(res: Login) {
    this.localStorageService.set(LocalStorageEnum.token, res.token);
    this.localStorageService.set(LocalStorageEnum.refresh_token, res.refresh_token);
    const rcp = res?.require_password_change ? res.require_password_change.toString() : null;
    this.localStorageService.set(LocalStorageEnum.required_change_password, rcp);
  }

  verifyOtp(data: VerifyOTP): Observable<Login> {
    return this.requestService.postJSON<Login>('/studio/account/login_verify_code', { data });
  }

  logout(): Observable<string> {
    this.localStorageService.delete(LocalStorageEnum.token);
    this.localStorageService.delete(LocalStorageEnum.refresh_token);
    this.localStorageService.delete(LocalStorageEnum.permissions);
    this.localStorageService.delete(LocalStorageEnum.required_change_password);
    this.tableStateService.clearAllStates();
    return new Observable<string>(observer => {
      observer.complete(); // complete function will be called when the observable is complete
      this.markStatusChange();
    });
  }

  private markStatusChange() {
    this.authChange$.next(this.isAuth);
    this.authStatus = this.isAuth;
  }

  get requiredPassword(): boolean {
    const rcpRawValue = this.localStorageService.get(LocalStorageEnum.required_change_password);
    const rcpValue = rcpRawValue === '1' ? +rcpRawValue : null;
    return rcpValue === 1;
  }
  get isAuth(): boolean {
    return this.localStorageService.get(LocalStorageEnum.token) && !this.requiredPassword ? true : false;
  }
}
